import { Button, ButtonGroup } from "@shopify/polaris";
import { PlusIcon, MinusIcon, ResetIcon } from "@shopify/polaris-icons";
import { useCallback, useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const STEP = 1.5;

// Image you can zoom (wheel, pinch, double-click, buttons) and pan (drag).
// Transform origin is the top-left corner, so x/y are the image's offset in
// px inside the frame; clamping keeps the image covering the frame.
export default function ZoomableImage({ src, alt, style }) {
  const frameRef = useRef(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  const clamp = useCallback((scale, x, y) => {
    const frame = frameRef.current;
    if (!frame) return { scale, x, y };
    const w = frame.clientWidth;
    const h = frame.clientHeight;
    return {
      scale,
      x: Math.min(0, Math.max(w - w * scale, x)),
      y: Math.min(0, Math.max(h - h * scale, y)),
    };
  }, []);

  // Zooms keeping the frame point (cx, cy) fixed under the cursor/fingers.
  const zoomAt = useCallback(
    (nextScale, cx, cy, from = viewRef.current) => {
      const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const ratio = scale / from.scale;
      setView(clamp(scale, cx - (cx - from.x) * ratio, cy - (cy - from.y) * ratio));
    },
    [clamp]
  );

  const zoomCenter = (factor) => {
    const frame = frameRef.current;
    zoomAt(viewRef.current.scale * factor, frame.clientWidth / 2, frame.clientHeight / 2);
  };

  const localPoint = (e) => {
    const rect = frameRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  // Wheel needs a non-passive listener so the modal doesn't scroll.
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;
    const onWheel = (e) => {
      e.preventDefault();
      const p = localPoint(e);
      zoomAt(viewRef.current.scale * Math.exp(-e.deltaY * 0.002), p.x, p.y);
    };
    frame.addEventListener("wheel", onWheel, { passive: false });
    return () => frame.removeEventListener("wheel", onWheel);
  }, [zoomAt]);

  useEffect(() => setView({ scale: 1, x: 0, y: 0 }), [src]);

  const startGesture = () => {
    const pts = [...pointers.current.values()];
    const v = viewRef.current;
    if (pts.length === 1) {
      gesture.current = { type: "pan", start: pts[0], view: v };
    } else if (pts.length === 2) {
      const [a, b] = pts;
      gesture.current = {
        type: "pinch",
        dist: Math.hypot(a.x - b.x, a.y - b.y),
        view: v,
      };
    } else {
      gesture.current = null;
    }
  };

  const onPointerDown = (e) => {
    frameRef.current.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, localPoint(e));
    startGesture();
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, localPoint(e));
    const g = gesture.current;
    if (!g) return;
    const pts = [...pointers.current.values()];
    if (g.type === "pan") {
      const p = pts[0];
      setView(clamp(g.view.scale, g.view.x + p.x - g.start.x, g.view.y + p.y - g.start.y));
    } else if (g.type === "pinch" && pts.length === 2) {
      const [a, b] = pts;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      zoomAt(g.view.scale * (dist / g.dist), (a.x + b.x) / 2, (a.y + b.y) / 2, g.view);
    }
  };

  const onPointerUp = (e) => {
    pointers.current.delete(e.pointerId);
    startGesture();
  };

  const onDoubleClick = (e) => {
    const p = localPoint(e);
    if (viewRef.current.scale > 1) setView({ scale: 1, x: 0, y: 0 });
    else zoomAt(3, p.x, p.y);
  };

  const zoomed = view.scale > 1;

  return (
    <div style={{ position: "relative", ...style }}>
      <div
        ref={frameRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onDoubleClick={onDoubleClick}
        style={{
          overflow: "hidden",
          borderRadius: 8,
          aspectRatio: "1 / 1",
          background: "var(--p-color-bg-surface-secondary)",
          touchAction: "none",
          cursor: zoomed ? "grab" : "zoom-in",
          userSelect: "none",
        }}
      >
        <img
          src={src}
          alt={alt}
          draggable={false}
          style={{
            display: "block",
            width: "100%",
            height: "100%",
            objectFit: "contain",
            transformOrigin: "0 0",
            transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
          }}
        />
      </div>
      <div style={{ position: "absolute", top: 8, right: 8 }}>
        <ButtonGroup variant="segmented">
          <Button icon={MinusIcon} accessibilityLabel="Alejar" disabled={!zoomed} onClick={() => zoomCenter(1 / STEP)} />
          <Button icon={PlusIcon} accessibilityLabel="Acercar" disabled={view.scale >= MAX_SCALE} onClick={() => zoomCenter(STEP)} />
          <Button icon={ResetIcon} accessibilityLabel="Restablecer" disabled={!zoomed} onClick={() => setView({ scale: 1, x: 0, y: 0 })} />
        </ButtonGroup>
      </div>
    </div>
  );
}
